/**
 * User API Service
 *
 * Handles all user-related API calls (profile, demographics, account management).
 * Routes through the appcore-api passthrough layer (/api/v1/external/ prefix).
 *
 * @module services/api/UserApiService
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { UserProfileDetailed } from '@/types/user';
import { getApiBaseUrl } from '@/config/api';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { SESSION_EXPIRED_EVENT } from '@/contexts/AuthContext';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
// In embed iframes (e.g. /community?embed=1), CommunicationBridge is per-frame and may not be
// populated yet; fallback to all known localStorage keys and sync back to CommunicationBridge
// so Community-API (and Istio) receive a Bearer token and allow GET /api/v1/external/user/ref_user_id/*
api.interceptors.request.use(
  async (config) => {
    let token = CommunicationBridge.getAccessToken();

    if (!token) {
      token =
        localStorage.getItem('gcip_access_token') ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('mock_access_token') ||
        localStorage.getItem('token_paste_access_token') ||
        null;
      if (token) {
        CommunicationBridge.setAccessToken(token);
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // AuthContext listens for this and will logout + redirect to login
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    } else if (error.response?.status === 403) {
      const serverMessage =
        error.response?.data?.message ||
        error.response?.data ||
        'Access denied: You can only access your own user data.';
      throw new Error(serverMessage);
    }
    return Promise.reject(error);
  }
);

export class UserApiService {
  /**
   * Fetch user data from the ref_user_id endpoint.
   *
   * Single source of truth for all profile fields (lore_user_name, sso_object_id,
   * email, account_verified_flag, avatar_url). The /profile endpoint has been
   * removed (APP-966).
   *
   * PII fields returned by the API are stripped by ProfileContext before storage.
   *
   * @param userId - User ID to fetch data for
   * @returns User data (PII stripped downstream by ProfileContext)
   */
  static async getUserData(userId: string | number): Promise<UserProfileDetailed> {
    try {
      const response: AxiosResponse<UserProfileDetailed> = await api.get(
        `/api/v1/external/user/ref_user_id/${userId}`,
        {
          headers: {
            'X-User-ID': userId.toString(),
          },
        }
      );

      if (response && response.data) {
        return response.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete all LoreBot data for a user
   * 
   * @param userId - User ID
   */
  static async deleteLoreBotData(userId: string | number): Promise<void> {
    try {
      await api.delete('/api/v1/external/user/lorebot-conversation', {
        headers: {
          'X-User-ID': userId.toString(),
        },
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Request account deletion for a user
   * 
   * Uses DELETE /api/v1/external/user?del_ref_user_id={userId} to match the
   * appcore-api passthrough signature (aligns with iOS and Android).
   * 
   * @param userId - User ID
   */
  static async requestAccountDeletion(userId: string | number): Promise<void> {
    try {
      await api.delete(`/api/v1/external/user`, {
        params: {
          del_ref_user_id: userId,
        },
        headers: {
          'X-User-ID': userId.toString(),
        },
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get verification status for a user
   *
   * Returns null if no verification data exists (404)
   * Token is automatically included via interceptor
   *
   * Note: This endpoint is on the verification service, not appcore-api
   *
   * @returns Full verification data including input fields, or null if not found
   */
  static async getVerificationStatus(): Promise<{
    is_verified: boolean;
    is_enabled: boolean;
    input_first_name?: string;
    input_last_name?: string;
    input_birth_date?: string;
    input_email?: string;
  } | null> {
    try {
      // Verification service base URL (separate from appcore-api)
      const verificationApiBaseUrl = 
        process.env.REACT_APP_VERIFICATION_API_BASE_URL ||
        (process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === 'prod'
          ? 'https://verification-api.lorehealthcare.com'
          : 'https://lore-verification-service-eex6i6xwzq-uc.a.run.app');
      
      // Create a separate axios instance for verification service
      const token = CommunicationBridge.getAccessToken();
      const verificationApi = axios.create({
        baseURL: verificationApiBaseUrl,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      const response = await verificationApi.get('/api/verification/status');
      
      return {
        is_verified: response.data?.is_verified ?? false,
        is_enabled: response.data?.is_enabled ?? false,
        input_first_name: response.data?.input_first_name || undefined,
        input_last_name: response.data?.input_last_name || undefined,
        input_birth_date: response.data?.input_birth_date || undefined,
        input_email: response.data?.input_email || undefined,
      };
    } catch (error) {
      // Return null if no verification data exists (404) or other errors
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      return null;
    }
  }

  /**
   * Get invitation/sharing link for user referrals
   *
   * Uses the recruitment-api endpoint (matching iOS implementation)
   *
   * @param userId - User ID to generate invite link for
   * @returns Invitation link data with original_url, coupon_code, and text_msg
   */
  static async getInviteLink(userId: string | number): Promise<{
    original_url: string;
    coupon_code: string;
    text_msg: string;
  }> {
    // Check if running in development mode (needed in both try and catch blocks)
    const isDevelopment = process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost';
    
    try {
      const token = CommunicationBridge.getAccessToken();
      
      // In local development, use proxy (via setupProxy.js) to avoid CORS issues
      // In production, call recruitment-api directly to avoid HTTPRoute cross-namespace issues
      let axiosInstance;
      if (isDevelopment) {
        // Local dev: use proxy (no baseURL, proxy handles routing)
        axiosInstance = axios.create({
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
      } else {
        // Production: call directly
        // Use REACT_APP_ENV set during deployment to determine environment
        const env = process.env.REACT_APP_ENV || 'development';
        const isProd = env === 'production' || env === 'prod';
        
        const recruitmentApiBaseUrl = isProd
          ? 'https://svcs-lore-00.endpoints.protected-00-349215.cloud.goog/services/recruitment-api'
          : 'https://svcs-lore-00-nonprod.endpoints.protected-00-349215.cloud.goog/services/recruitment-api';
        
        axiosInstance = axios.create({
          baseURL: recruitmentApiBaseUrl,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
      }

      // Use the recruitment-api endpoint (matching iOS)
      // In local dev, proxy adds /services/recruitment-api prefix
      // In production, baseURL already includes /services/recruitment-api
      const endpoint = isDevelopment 
        ? '/services/recruitment-api/api/v1/links/generate-sharing-link'
        : '/api/v1/links/generate-sharing-link';
      
      const response = await axiosInstance.post(endpoint, {
        community_ref_user_id: parseInt(userId.toString(), 10),
      });

      return response.data;
    } catch (error: any) {
      // For local development, use demo data if API returns RBAC error or CORS error
      if (isDevelopment && (error.response?.status === 403 || error.code === 'ERR_NETWORK' || error.message?.includes('CORS'))) {
        return {
          original_url: 'https://getlore.link/',
          coupon_code: 'DEMO-LOCAL-TEST',
          text_msg: '[LOCAL DEV] Join me on Lore! Use this invite link to get started.',
        };
      }
      throw error;
    }
  }
}

